import { Controller } from "@hotwired/stimulus"

// 銀行・支店のインクリメンタルサーチコントローラ
export default class extends Controller {
  static targets = [
    "bankInput", "bankCode", "bankDropdown", "bankList", "bankDisplay",
    "branchInput", "branchCode", "branchDropdown", "branchList", "branchDisplay"
  ]

  static values = {
    banksUrl: { type: String, default: "/api/banks" },
    branchesUrlTemplate: { type: String, default: "/api/banks/{id}/branches" }
  }

  connect() {
    this.selectedBankCode = this.bankCodeTarget.value || null
    this.debounceTimer = null

    // 初期表示の更新
    if (this.selectedBankCode && this.hasBankDisplayTarget) {
      this.updateBankDisplay()
    }
    if (this.branchCodeTarget.value && this.hasBranchDisplayTarget) {
      this.updateBranchDisplay()
    }

    // クリック外で閉じる
    document.addEventListener("click", this.handleClickOutside.bind(this))
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside.bind(this))
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.closeBankDropdown()
      this.closeBranchDropdown()
    }
  }

  // 銀行検索
  searchBanks(event) {
    const query = event.target.value.trim()

    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      this.fetchBanks(query)
    }, 200)
  }

  async fetchBanks(query) {
    try {
      const url = `${this.banksUrlValue}?q=${encodeURIComponent(query)}`
      const response = await fetch(url)
      const banks = await response.json()

      this.renderBankList(banks)
      this.openBankDropdown()
    } catch (error) {
      console.error("銀行検索エラー:", error)
    }
  }

  renderBankList(banks) {
    if (banks.length === 0) {
      this.bankListTarget.innerHTML = `
        <div class="px-4 py-2 text-sm text-gray-500">該当する銀行がありません</div>
      `
      return
    }

    this.bankListTarget.innerHTML = banks.map(bank => `
      <button type="button"
              class="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
              data-action="click->bank-search#selectBank"
              data-bank-code="${bank.code}"
              data-bank-name="${bank.name_full}">
        <span class="font-medium">${bank.name_full}</span>
        <span class="text-gray-500 ml-2">(${bank.code})</span>
      </button>
    `).join("")
  }

  selectBank(event) {
    event.preventDefault()
    const code = event.currentTarget.dataset.bankCode
    const name = event.currentTarget.dataset.bankName

    this.bankCodeTarget.value = code
    this.bankInputTarget.value = name
    this.selectedBankCode = code

    if (this.hasBankDisplayTarget) {
      this.bankDisplayTarget.textContent = `${name} (${code})`
      this.bankDisplayTarget.classList.remove("hidden")
    }

    this.closeBankDropdown()

    // 支店をクリア
    this.branchCodeTarget.value = ""
    this.branchInputTarget.value = ""
    if (this.hasBranchDisplayTarget) {
      this.branchDisplayTarget.textContent = ""
      this.branchDisplayTarget.classList.add("hidden")
    }

    // 支店入力にフォーカス
    this.branchInputTarget.focus()
  }

  openBankDropdown() {
    this.bankDropdownTarget.classList.remove("hidden")
  }

  closeBankDropdown() {
    this.bankDropdownTarget.classList.add("hidden")
  }

  // 支店検索
  searchBranches(event) {
    if (!this.selectedBankCode) {
      this.branchListTarget.innerHTML = `
        <div class="px-4 py-2 text-sm text-gray-500">先に銀行を選択してください</div>
      `
      this.openBranchDropdown()
      return
    }

    const query = event.target.value.trim()

    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      this.fetchBranches(query)
    }, 200)
  }

  async fetchBranches(query) {
    try {
      const url = this.branchesUrlTemplateValue.replace("{id}", this.selectedBankCode) + `?q=${encodeURIComponent(query)}`
      const response = await fetch(url)
      const branches = await response.json()

      this.renderBranchList(branches)
      this.openBranchDropdown()
    } catch (error) {
      console.error("支店検索エラー:", error)
    }
  }

  renderBranchList(branches) {
    if (branches.length === 0) {
      this.branchListTarget.innerHTML = `
        <div class="px-4 py-2 text-sm text-gray-500">該当する支店がありません</div>
      `
      return
    }

    this.branchListTarget.innerHTML = branches.map(branch => `
      <button type="button"
              class="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
              data-action="click->bank-search#selectBranch"
              data-branch-code="${branch.code}"
              data-branch-name="${branch.name_full}">
        <span class="font-medium">${branch.name_full}</span>
        <span class="text-gray-500 ml-2">(${branch.code})</span>
      </button>
    `).join("")
  }

  selectBranch(event) {
    event.preventDefault()
    const code = event.currentTarget.dataset.branchCode
    const name = event.currentTarget.dataset.branchName

    this.branchCodeTarget.value = code
    this.branchInputTarget.value = name

    if (this.hasBranchDisplayTarget) {
      this.branchDisplayTarget.textContent = `${name} (${code})`
      this.branchDisplayTarget.classList.remove("hidden")
    }

    this.closeBranchDropdown()
  }

  openBranchDropdown() {
    this.branchDropdownTarget.classList.remove("hidden")
  }

  closeBranchDropdown() {
    this.branchDropdownTarget.classList.add("hidden")
  }

  // 銀行入力欄フォーカス時
  focusBankInput() {
    if (this.bankInputTarget.value.trim() === "") {
      this.fetchBanks("")
    }
  }

  // 支店入力欄フォーカス時
  focusBranchInput() {
    if (this.branchInputTarget.value.trim() === "" && this.selectedBankCode) {
      this.fetchBranches("")
    } else if (!this.selectedBankCode) {
      this.branchListTarget.innerHTML = `
        <div class="px-4 py-2 text-sm text-gray-500">先に銀行を選択してください</div>
      `
      this.openBranchDropdown()
    }
  }

  updateBankDisplay() {
    // 初期表示用（ページロード時）
  }

  updateBranchDisplay() {
    // 初期表示用（ページロード時）
  }
}
